/**
 * Estimating 2 tab — the Mechanical Takeoff module shell. Owns the view
 * switch (Home / Plans & Takeoffs / Estimate), the module header, job
 * dialogs, and active-time tracking (time accrues toward the open job
 * everywhere in the module, Home included).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TakeoffProvider, useTakeoff } from "./store";
import { useActiveTime, onTimeTick } from "./timeTracker";
import { getMyTime, getNoteVersions, ackNoteVersion } from "./api";
import ModuleHeader from "./ModuleHeader";
import NoteAlertDialog from "./NoteAlertDialog";
import HomeView from "./HomeView";
import WorkspaceView from "./WorkspaceView";
import EstimateView from "./EstimateView";
import { NewProjectDialog, FindJobsDialog } from "./dialogs";
import { FONT, MONO } from "./theme";

/** Bottom-right live job timer: job name, saved + live elapsed with seconds,
 * explicit Running/Paused. Visible on Home, drawings and estimates. */
function TimerPill() {
  const { activeId, activeProject } = useTakeoff();
  const [live, setLive] = useState({ seconds: 0, running: false });
  const [saved, setSaved] = useState(0);
  useEffect(() => {
    setLive({ seconds: 0, running: false });
    setSaved(0);
    if (!activeId) return undefined;
    getMyTime(activeId).then((d) => setSaved(d.total_seconds || 0)).catch(() => {});
    return onTimeTick((t) => { if (t.projectId === activeId) setLive({ seconds: t.seconds, running: t.running }); });
  }, [activeId]);
  if (!activeId) return null;
  const total = saved + Math.floor(live.seconds);
  const h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
  const txt = h ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
  return (
    <div className="fixed bottom-3 right-3 z-40 flex items-center gap-2 rounded-full border border-slate-200 bg-white/95 px-3 py-1.5 shadow-lg backdrop-blur"
      data-testid="takeoff-timer-pill" title="Time tracked on this job">
      <span className={`h-2 w-2 shrink-0 rounded-full ${live.running ? "bg-emerald-500" : "bg-amber-400"}`} />
      <span className="max-w-[110px] truncate text-[11px] font-semibold text-slate-700">{activeProject?.name || "Job"}</span>
      <span className={`${MONO} text-xs tabular-nums text-slate-900`} data-testid="takeoff-timer-value">{txt}</span>
      <span className={`text-[9px] font-bold uppercase tracking-widest ${live.running ? "text-emerald-600" : "text-amber-500"}`}
        data-testid="takeoff-timer-state">{live.running ? "Running" : "Paused"}</span>
    </div>
  );
}

function Shell() {
  const {
    activeId, setActiveId, activeProject, workspace, statuses,
    createProject, updateProject, wsError, loadWorkspace, viewer,
  } = useTakeoff();
  const [view, setView] = useState("home");
  const [ctxLine, setCtxLine] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [exportScope, setExportScope] = useState({});
  const [noteAlert, setNoteAlert] = useState(null); // {projectId, projectName, versions}
  useActiveTime(activeId, !!activeId);

  // NEW NOTE auto-popup (item 7): opening a job with unread note versions
  // (within the 7-day window) opens them automatically — on every open, not
  // just the first. Closing without acknowledging leaves them unread, so
  // they reappear on the next open.
  const checkNotes = (pid) => {
    getNoteVersions(pid, true).then((d) => {
      if (!d.versions?.length) { setNoteAlert(null); return; }
      const proj = (workspace?.projects || []).find((x) => x.project_id === pid);
      setNoteAlert({ projectId: pid, projectName: proj?.name || "Job", versions: d.versions });
    }).catch(() => {});
  };
  useEffect(() => {
    if (activeId) checkNotes(activeId);
    else setNoteAlert(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const ackNote = async (versionId) => {
    if (!noteAlert) return;
    await ackNoteVersion(noteAlert.projectId, versionId);
    const versions = noteAlert.versions.map((v) => (v.version_id === versionId ? { ...v, read: true } : v));
    if (versions.every((v) => v.read)) {
      setNoteAlert(null);
      loadWorkspace(); // badge flips to READ on Home
    } else {
      setNoteAlert({ ...noteAlert, versions });
    }
  };

  const openProject = (pid) => {
    if (pid === activeId) checkNotes(pid); // re-opening the same job re-alerts while unread
    setActiveId(pid);
    setView("plans");
  };
  const navigate = (v) => {
    if (v !== "home" && !activeId) {
      toast.message("Pick a job first — use the selector or Find Jobs.");
      return;
    }
    setView(v);
  };

  if (wsError) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-3 bg-[#e8edf4] p-6" style={FONT}>
        <p className="rounded-xl border border-red-200 bg-white px-6 py-4 text-sm text-red-600 shadow-sm" data-testid="takeoff-ws-error">{wsError}</p>
        <button onClick={loadWorkspace} data-testid="takeoff-ws-retry"
          className="rounded-md bg-[#f97316] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-[#ea580c]">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-full flex-col bg-[#e8edf4] text-slate-800"
      style={{ ...FONT, backgroundImage: "radial-gradient(1200px 500px at 80% -10%, rgba(249,115,22,0.07), transparent)" }}
      data-testid="takeoff-module"
    >
      <ModuleHeader
        view={view}
        onNavigate={navigate}
        contextLine={[activeProject?.name, ctxLine].filter(Boolean).join(" · ")}
        onNewJob={() => setNewOpen(true)}
        onFindJobs={() => setFindOpen(true)}
        exportScope={exportScope}
      />
      <div className="min-h-0 flex-1">
        {view === "home" && <HomeView onOpenProject={openProject} onNewJob={() => setNewOpen(true)} />}
        {view === "plans" && activeId && <WorkspaceView onContext={setCtxLine} />}
        {view === "estimate" && activeId && (viewer ? (
          <div className="flex min-h-[300px] items-center justify-center p-8" data-testid="takeoff-estimate-blocked">
            <p className="max-w-sm text-center text-sm text-slate-500 dark:text-white/60">
              Estimates are prepared by the office. Drawings, specs and quantities are under Plans &amp; Takeoffs.
            </p>
          </div>
        ) : (
          <EstimateView onScopeChange={setExportScope} />
        ))}
      </div>

      {!viewer && (
      <NewProjectDialog open={newOpen} onClose={() => setNewOpen(false)}
        onCreate={async (p) => { const proj = await createProject(p); toast.success(`Job "${proj.name}" created`); openProject(proj.project_id); }} />
      )}
      <FindJobsDialog open={findOpen} onClose={() => setFindOpen(false)}
        projects={workspace?.projects || []} statuses={statuses}
        onOpen={openProject}
        onStatus={viewer ? undefined : (pid, status) => updateProject(pid, { status })} />
      {/* View Only estimating never shows the job timer (phone + desktop);
          regular estimating keeps it. Time records are untouched. */}
      {!viewer && <TimerPill />}
      <NoteAlertDialog open={!!noteAlert} onClose={() => setNoteAlert(null)}
        projectName={noteAlert?.projectName} versions={noteAlert?.versions || []} onAck={ackNote} />
    </div>
  );
}

export default function TakeoffTab({ design = false }) {
  return (
    <TakeoffProvider design={design}>
      <Shell />
    </TakeoffProvider>
  );
}
